import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  ClipboardCheck, 
  CheckCircle2, 
  FileText, 
  Users, 
  Building2,
  MapPin,
  Eye,
  Shield,
  MessageSquare,
  Target,
  Lightbulb,
  Award
} from 'lucide-react';

// Für wen ist das Audit sinnvoll
const targetGroups = [
  { icon: Users, text: 'Träger ohne eigenes QM-Personal' },
  { icon: Building2, text: 'Kleine und mittlere Bildungsträger' },
  { icon: ClipboardCheck, text: 'Träger mit anstehendem Überprüfungsaudit' },
  { icon: MapPin, text: 'Träger mit mehreren Maßnahmen oder Standorten' },
  { icon: FileText, text: 'Träger, die ihre Dokumentation prüfen lassen wollen' }
];

// Was Sie nach dem Audit erhalten
const auditResults = [
  'Vollständiger Auditbericht',
  'Dokumentierte Feststellungen',
  'Konkreter Maßnahmenplan',
  'Nachweise für das FKS-Audit',
  'Klare Handlungsempfehlungen'
];

// Vorteile eines externen internen Audits
const externalBenefits = [
  { icon: Eye, text: 'Neutrale Sicht von außen' },
  { icon: Lightbulb, text: 'Keine Betriebsblindheit' },
  { icon: FileText, text: 'FKS-taugliche Dokumentation' },
  { icon: Users, text: 'Entlastung des Teams' },
  { icon: Target, text: 'Klare Struktur für kommende Audits' }
];

// Ablauf des Audits
const processSteps = [
  {
    number: '01',
    title: 'Kurzes Vorgespräch',
    description: 'Klärung von Größe, Maßnahmen und Struktur des Trägers.'
  },
  {
    number: '02',
    title: 'Dokumentenprüfung',
    description: 'Durchsicht der vorhandenen QM-Unterlagen.'
  },
  {
    number: '03',
    title: 'Auditgespräch',
    description: 'Strukturiertes Audit mit der Geschäftsführung oder QM-Verantwortlichen.'
  },
  {
    number: '04',
    title: 'Bericht und Maßnahmenplan',
    description: 'Sie erhalten einen vollständigen Auditbericht mit konkreten Handlungsschritten.'
  }
];

// Warum MEHRAZAV
const trustReasons = [
  'Erfahrung aus realem AZAV-Trägerbetrieb',
  'Erfolgreich bestandene Audits ohne Abweichungen',
  'Praxisnahe Lösungen statt Theorie',
  'Direkte und klare Kommunikation'
];

const InternesAudit = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="internes-audit-page">
      <Navbar />
      <div className="hero-glow" />
      <main className="pt-20 relative">
        
        {/* 1. Hero Section - Lebendiges Layout */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="text-primary text-sm font-semibold uppercase tracking-widest mb-6 block">
                  Jährliche Pflicht für Bildungsträger
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.1] mb-8" data-testid="internes-audit-headline">
                  Internes{' '}
                  <span className="text-primary">AZAV-Audit</span>{' '}
                  professionell durchführen
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                  Pflicht erfüllen, Risiken vermeiden und auditfest bleiben. Wir übernehmen Ihr jährliches internes Audit – strukturiert, dokumentiert und FKS-tauglich.
                </p>
                <Link to="/kontakt">
                  <Button 
                    className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                    data-testid="hero-cta-button"
                  >
                    Internes Audit anfragen
                  </Button>
                </Link>
              </motion.div>

              {/* Right Stats */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="hidden lg:block"
              >
                <div className="bg-card/20 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-5 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ClipboardCheck className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">Vollständiger Auditbericht</div>
                        <div className="text-sm text-muted-foreground">Dokumentiert und nachvollziehbar</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-5 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">Konkreter Maßnahmenplan</div>
                        <div className="text-sm text-muted-foreground">Mit Fristen und Verantwortlichkeiten</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-5 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">FKS-tauglich</div>
                        <div className="text-sm text-muted-foreground">Bereit für externe Prüfungen</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 2. Pflichtcharakter erklären - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight mb-8 text-gray-900">
                Das interne Audit ist verpflichtend
              </h2>
              <div className="space-y-5 text-gray-600 leading-relaxed text-lg">
                <p>
                  Jeder AZAV-zugelassene Bildungsträger ist verpflichtet, regelmäßig ein internes Audit durchzuführen und zu dokumentieren.
                </p>
                <p>
                  Fehlende oder unzureichende interne Audits führen bei Überprüfungen häufig zu Abweichungen.
                </p>
                <p className="text-gray-900 font-medium">
                  Wir übernehmen diese Pflichtleistung für Sie – vollständig dokumentiert und auditfest.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 3. Für wen das Audit sinnvoll ist */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white">
                Für welche Träger ist ein externes internes Audit sinnvoll?
              </h2>
            </motion.div>
            
            <div className="flex flex-col gap-6">
              {targetGroups.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="flex items-center gap-4 p-5 bg-card/30 border border-white/5 rounded-xl"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-white">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Was Sie nach dem Audit erhalten - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Ihr Ergebnis nach dem internen Audit
              </h2>
            </motion.div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <ul className="space-y-5">
                {auditResults.map((result, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    className="flex items-center gap-4"
                  >
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-900">{result}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 5. Vorteile eines externen internen Audits */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white">
                Vorteile eines externen internen Audits
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {externalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="flex items-center gap-4 p-5 bg-card/30 border border-white/5 rounded-xl"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-white">{benefit.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Ablauf des internen Audits - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                So läuft das interne Audit ab
              </h2>
            </motion.div>

            <div className="flex flex-col gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-6 p-6 bg-white border border-gray-200 rounded-xl shadow-sm"
                >
                  <div className="text-3xl font-heading font-bold text-primary/60 flex-shrink-0 w-10">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Preislogik / Angebotsstruktur */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight mb-8 text-white">
                Einzelauftrag oder Jahrespaket
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed text-lg">
                <p>
                  Das interne Audit kann als Einzelauftrag oder im Rahmen eines laufenden QM-Pakets durchgeführt werden.
                </p>
                <p>
                  Gerne erstellen wir Ihnen ein individuelles Angebot auf Basis Ihrer Trägerstruktur.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 8. Vertrauensabschnitt - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Warum MEHRAZAV
              </h2>
            </motion.div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <ul className="space-y-5">
                {trustReasons.map((reason, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    className="flex items-center gap-4"
                  >
                    <Award className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-900">{reason}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 9. Abschluss-CTA */}
        <section className="py-24 md:py-32 bg-[#050505] relative">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-8">
                <MessageSquare className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight mb-6 text-white">
                Ihr internes Audit steht an?
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto mb-10">
                Lassen Sie uns kurz Ihre Situation besprechen. Sie erhalten eine klare Einschätzung und ein konkretes Angebot.
              </p>
              <Link to="/kontakt">
                <Button 
                  className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                  data-testid="footer-cta-button"
                >
                  Internes Audit anfragen
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default InternesAudit;
